import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react'
import SimpleMinimap from './SimpleMinimap'

interface IndividualViewerPanelProps {
  imageId: string
  imageName: string
  isSelected: boolean
  onSelect: () => void
  onRemove?: () => void
  showRemoveButton?: boolean
  onViewerReady?: (viewer: any) => void
  onZoomChange?: (imageId: string, zoom: number) => void
  onViewportChange?: (imageId: string, center: { x: number, y: number }, zoom: number) => void
  // Adjustments applied from parent (independent per image)
  adjustments?: {
    brightness: number
    contrast: number
    saturation: number
    gamma: number
    redChannel: number
    greenChannel: number
    blueChannel: number
  }
}

export interface IndividualViewerPanelRef {
  getViewer: () => any
  getSlideInfo: () => SlideInfo | null
}

interface SlideInfo {
  id: string
  name: string
  width: number
  height: number
  tile_size: number
  properties?: {
    'openslide.mpp-x'?: string
    'openslide.mpp-y'?: string
    'openslide.objective-power'?: string
    [key: string]: any
  }
  bounds?: {
    x: number
    y: number
    width: number
    height: number
  }
}

const IndividualViewerPanel = forwardRef<IndividualViewerPanelRef, IndividualViewerPanelProps>(({
  imageId,
  imageName,
  isSelected,
  onSelect,
  onRemove,
  showRemoveButton = true,
  onViewerReady,
  onZoomChange,
  onViewportChange,
  adjustments
}, ref) => {
  const viewerRef = useRef<HTMLDivElement>(null)
  const viewerInstanceRef = useRef<any>(null)
  const onZoomChangeRef = useRef(onZoomChange)
  const onViewportChangeRef = useRef(onViewportChange)
  const [slideInfo, setSlideInfo] = useState<SlideInfo | null>(null)
  const [viewerReady, setViewerReady] = useState(false)
  const [zoomInfo, setZoomInfo] = useState({ zoom: 0, level: 0, magnification: 0 })
  const [scaleBarWidth, setScaleBarWidth] = useState(100)
  const [scaleBarValueMicrons, setScaleBarValueMicrons] = useState(100)

  // Keep callbacks up to date
  useEffect(() => {
    onZoomChangeRef.current = onZoomChange
    onViewportChangeRef.current = onViewportChange
    console.log(`[IndividualViewerPanel ${imageId.slice(0, 8)}] Callbacks:`, {
      onZoomChange: onZoomChange ? 'PROVIDED' : 'NOT PROVIDED',
      onViewportChange: onViewportChange ? 'PROVIDED' : 'NOT PROVIDED'
    })
  }, [onZoomChange, onViewportChange, imageId])

  // Debug: Log when isSelected changes
  useEffect(() => {
    console.log(`[IndividualViewerPanel ${imageId.slice(0, 8)}] isSelected:`, isSelected)
  }, [isSelected, imageId])

  // Expose viewer and slide info to parent
  useImperativeHandle(ref, () => ({
    getViewer: () => viewerInstanceRef.current,
    getSlideInfo: () => slideInfo
  }))

  // Notify parent when viewer is ready
  useEffect(() => {
    if (viewerReady && viewerInstanceRef.current && onViewerReady) {
      onViewerReady(viewerInstanceRef.current)
    }
  }, [viewerReady, onViewerReady])

  // Initialize viewer
  useEffect(() => {
    if (!viewerRef.current || !imageId) return

    const loadSlide = async () => {
      try {
        // Wait for OpenSeadragon
        if (!(window as any).OpenSeadragon) {
          await new Promise((resolve) => {
            const checkOSD = setInterval(() => {
              if ((window as any).OpenSeadragon) {
                clearInterval(checkOSD)
                resolve(true)
              }
            }, 100)
          })
        }

        // Fetch slide info
        const slideResponse = await fetch(`/api/v1/imaging/slides/${imageId}/`)
        if (!slideResponse.ok) {
          console.error(`[IndividualViewer] Failed to fetch slide info for ${imageId}`)
          return
        }

        const slideData = await slideResponse.json()
        const width = slideData.width
        const height = slideData.height
        const tileSize = slideData.tile_size || 256

        const info: SlideInfo = {
          id: slideData.id,
          name: slideData.name,
          width: width,
          height: height,
          tile_size: tileSize,
          properties: slideData.properties,
          bounds: slideData.bounds
        }
        setSlideInfo(info)

        // Calculate DZI levels
        const maxDimension = Math.max(width, height)
        const dziLevels = Math.ceil(Math.log2(maxDimension / tileSize)) + 1

        // Custom tile source
        const getTileUrl = (level: number, x: number, y: number) => {
          return `/api/v1/imaging/standalone/dzi/${imageId}_files/${level}/${x}_${y}.jpeg`
        }

        const tileSource = {
          height: height,
          width: width,
          tileSize: tileSize,
          minLevel: 0,
          maxLevel: dziLevels - 1,
          getTileUrl: getTileUrl,
        }

        // Initialize viewer
        const viewer = new (window as any).OpenSeadragon({
          element: viewerRef.current,
          prefixUrl: '/openseadragon/images/',
          tileSources: tileSource,
          showNavigationControl: false,
          showNavigator: false,
          showRotationControl: false,
          showFullPageControl: false,
          defaultZoomLevel: 0,
          minZoomLevel: 0.5,
          maxZoomLevel: 100,
          visibilityRatio: 0.1,
          constrainDuringPan: false,
          animationTime: 0.3,
          immediateRender: true,
          preload: true,
          // Disable click-to-zoom gestures
          gestureSettingsMouse: {
            clickToZoom: false,      // Disable single click to zoom
            dblClickToZoom: false,   // Disable double click to zoom
            scrollToZoom: true,      // Keep mouse wheel zoom
            flickEnabled: true,      // Keep flick pan
            pinchToZoom: false       // Disable pinch zoom
          },
          gestureSettingsTouch: {
            clickToZoom: false,
            dblClickToZoom: false,
            scrollToZoom: false,
            flickEnabled: true,
            pinchToZoom: true        // Keep pinch zoom for touch devices
          }
        })

        viewerInstanceRef.current = viewer

        // Update zoom info and scale bar
        const updateZoomInfo = () => {
          console.log('[IndividualViewerPanel] updateZoomInfo called for', imageId.slice(0, 8))
          if (viewer && info) {
            const currentZoom = viewer.viewport.getZoom()
            const homeZoom = viewer.viewport.getHomeZoom()
            const zoomRatio = currentZoom / homeZoom
            const effectiveMagnification = zoomRatio

            console.log('[IndividualViewerPanel] Zoom info:', { imageId: imageId.slice(0, 8), currentZoom, homeZoom, zoomRatio })

            setZoomInfo({
              zoom: parseFloat(zoomRatio.toFixed(3)),
              level: 0,
              magnification: parseFloat(effectiveMagnification.toFixed(2)),
            })

            // Notify parent of zoom change (for sync lock)
            if (onZoomChangeRef.current) {
              console.log('[IndividualViewerPanel] Calling onZoomChange:', { imageId, zoomRatio })
              onZoomChangeRef.current(imageId, zoomRatio)
            } else {
              console.log('[IndividualViewerPanel] No onZoomChange callback provided')
            }

            // Scale bar calculation
            let calibration = 20.0
            if (info.properties) {
              const mppX = info.properties['openslide.mpp-x']
              const mppY = info.properties['openslide.mpp-y']
              if (mppX) {
                calibration = parseFloat(mppX)
              } else if (mppY) {
                calibration = parseFloat(mppY)
              }
            }

            const fixedPixelWidth = 200
            const micronsPerPixel = calibration / zoomRatio
            const scaleValueMicrons = (fixedPixelWidth * micronsPerPixel) / 2

            setScaleBarWidth(fixedPixelWidth)
            setScaleBarValueMicrons(scaleValueMicrons)
          }
        }

        viewer.addHandler('open', () => {
          setViewerReady(true)
          setTimeout(() => {
            updateZoomInfo()
            viewer.viewport.goHome(true)
          }, 200)
        })

        viewer.addHandler('zoom', updateZoomInfo)
        viewer.addHandler('animation-finish', updateZoomInfo)

        // Handle viewport change for pan sync
        viewer.addHandler('animation', () => {
          if (onViewportChangeRef.current && viewer && viewer.viewport) {
            const center = viewer.viewport.getCenter()
            const zoom = viewer.viewport.getZoom() / viewer.viewport.getHomeZoom()
            console.log('[IndividualViewerPanel] Viewport changed:', { imageId: imageId.slice(0, 8), center, zoom })
            onViewportChangeRef.current(imageId, { x: center.x, y: center.y }, zoom)
          }
        })

      } catch (error) {
        console.error(`[IndividualViewer] Failed to initialize viewer for ${imageId}:`, error)
      }
    }

    loadSlide()

    return () => {
      if (viewerInstanceRef.current) {
        viewerInstanceRef.current.destroy()
        viewerInstanceRef.current = null
      }
    }
  }, [imageId])

  // Apply adjustments via CSS filters (independent per image)
  useEffect(() => {
    if (!viewerRef.current || !viewerReady) return

    const canvas = viewerRef.current.querySelector('canvas')
    if (!canvas) return

    // Use default adjustments if not provided
    const adj = adjustments || {
      brightness: 100,
      contrast: 100,
      saturation: 100,
      gamma: 1,
      redChannel: 100,
      greenChannel: 100,
      blueChannel: 100
    }

    const filters: string[] = []

    // Always apply filters (even at 100% to ensure consistency)
    filters.push(`brightness(${adj.brightness / 100})`)
    filters.push(`contrast(${adj.contrast / 100})`)
    filters.push(`saturate(${adj.saturation / 100})`)

    // Gamma approximation
    if (adj.gamma !== 1) {
      const gammaValue = Math.pow(1.5, adj.gamma - 1)
      filters.push(`brightness(${gammaValue})`)
    }

    // RGB channels via SVG filter
    const needsRGBFilter = adj.redChannel !== 100 ||
                          adj.greenChannel !== 100 ||
                          adj.blueChannel !== 100

    if (needsRGBFilter) {
      const filterId = `osd-rgb-filter-${imageId}`
      let svgContainer = document.getElementById(`osd-rgb-svg-${imageId}`) as SVGSVGElement | null

      if (!svgContainer) {
        svgContainer = document.createElementNS('http://www.w3.org/2000/svg', 'svg') as SVGSVGElement
        svgContainer.id = `osd-rgb-svg-${imageId}`
        svgContainer.style.position = 'absolute'
        svgContainer.style.width = '0'
        svgContainer.style.height = '0'
        document.body.appendChild(svgContainer)
      }

      svgContainer.innerHTML = ''
      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
      const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter')
      filter.id = filterId

      const colorMatrix = document.createElementNS('http://www.w3.org/2000/svg', 'feColorMatrix')
      colorMatrix.setAttribute('type', 'matrix')

      const r = adj.redChannel / 100
      const g = adj.greenChannel / 100
      const b = adj.blueChannel / 100

      const matrix = `${r} 0 0 0 0  0 ${g} 0 0 0  0 0 ${b} 0 0  0 0 0 1 0`
      colorMatrix.setAttribute('values', matrix)

      filter.appendChild(colorMatrix)
      defs.appendChild(filter)
      svgContainer.appendChild(defs)

      filters.push(`url(#${filterId})`)
    } else {
      // Clean up SVG filter if not needed
      const svgContainer = document.getElementById(`osd-rgb-svg-${imageId}`)
      if (svgContainer) {
        svgContainer.remove()
      }
    }

    canvas.style.filter = filters.join(' ')
  }, [adjustments, imageId, viewerReady])


  return (
    <div
      className={`relative bg-gray-900 rounded overflow-hidden border-4 transition-all ${
        isSelected ? 'border-orange-500 shadow-xl shadow-orange-500/50' : 'border-gray-700'
      }`}
      onClick={(e) => {
        // Click on container (but not on controls) should select
        const target = e.target as HTMLElement
        // Only select if clicking on the container itself or the viewer area
        if (!target.closest('button') && !target.closest('.minimap')) {
          console.log('[IndividualViewerPanel] Container clicked for imageId:', imageId)
          onSelect()
        }
      }}
    >
      {/* Selected indicator - top right */}
      {isSelected && (
        <div className="absolute top-2 right-2 z-30 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded">
          SELECTED
        </div>
      )}

      {/* Remove button - top right (when not selected) */}
      {!isSelected && showRemoveButton && onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="absolute top-2 right-2 z-30 text-white hover:text-red-400 bg-black bg-opacity-50 p-1 rounded"
          title="Remove from view"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      {/* OpenSeadragon Viewer */}
      <div ref={viewerRef} className="w-full h-full" />

      {/* Minimap - Top left corner, very small */}
      {viewerReady && viewerInstanceRef.current && slideInfo && (
        <div className="absolute top-2 left-2 z-20 transform scale-50 origin-top-left">
          <SimpleMinimap
            viewer={viewerInstanceRef.current}
            imageId={imageId}
            bounds={slideInfo.bounds}
            dimensions={{ width: slideInfo.width, height: slideInfo.height }}
          />
        </div>
      )}

      {/* Image Info - Bottom Right (with name, zoom, size) */}
      {viewerReady && slideInfo && (
        <div className="absolute bottom-2 right-2 z-20 bg-black bg-opacity-90 px-2 py-1.5 rounded text-xs text-white max-w-[200px]">
          <div className="space-y-0.5">
            <div className="font-semibold truncate text-orange-400">
              {imageName || `Slide ${imageId.slice(0, 8)}`}
            </div>
            <div>
              <span className="font-semibold">Zoom:</span> {zoomInfo.zoom.toFixed(1)}×
            </div>
            <div>
              <span className="font-semibold">Size:</span> {slideInfo.width.toLocaleString()} × {slideInfo.height.toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {/* Scale Bar - Bottom Center */}
      {viewerReady && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20">
          <div className="flex flex-col items-center">
            <div className="relative" style={{ width: `${scaleBarWidth}px`, height: '25px' }}>
              {/* Main horizontal line */}
              <div className="absolute bottom-6 left-0 right-0 h-0.5 bg-white"></div>

              {/* Scale marks */}
              {[0, 1, 2, 3, 4, 5].map((i) => {
                const position = (scaleBarWidth / 5) * i
                const markValueInMicrons = (scaleBarValueMicrons / 5) * i

                let displayValue = ''
                if (scaleBarValueMicrons >= 1000) {
                  const markValueInMm = markValueInMicrons / 1000
                  if (i === 5) {
                    displayValue = Number.isInteger(markValueInMm)
                      ? `${markValueInMm}mm`
                      : `${markValueInMm.toFixed(1)}mm`
                  } else {
                    displayValue = Number.isInteger(markValueInMm)
                      ? `${markValueInMm}`
                      : `${markValueInMm.toFixed(1)}`
                  }
                } else if (scaleBarValueMicrons >= 1) {
                  if (i === 5) {
                    displayValue = Number.isInteger(markValueInMicrons)
                      ? `${markValueInMicrons}μm`
                      : `${markValueInMicrons.toFixed(1)}μm`
                  } else {
                    displayValue = Number.isInteger(markValueInMicrons)
                      ? `${markValueInMicrons}`
                      : `${markValueInMicrons.toFixed(1)}`
                  }
                } else {
                  const markValueInNm = markValueInMicrons * 1000
                  if (i === 5) {
                    displayValue = `${Math.round(markValueInNm)}nm`
                  } else {
                    displayValue = `${Math.round(markValueInNm)}`
                  }
                }

                return (
                  <div key={i} className="absolute bottom-6" style={{ left: `${position}px` }}>
                    <div className="absolute -translate-x-1/2 w-0.5 h-2 bg-white"></div>
                    <div className="absolute -translate-x-1/2 top-2 text-[10px] font-semibold text-white whitespace-nowrap">
                      {displayValue}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
})

IndividualViewerPanel.displayName = 'IndividualViewerPanel'

export default IndividualViewerPanel
